// ProofDrill — step-by-step derivation prompts (STEM / discrete-math / CS).
//
// Decision history:
//   Session 19D: added to fill the STEM gap noted in Session 19B —
//     CS3052 algorithm correctness proofs and discrete-math derivations don't
//     fit MCQ or essay shapes; they need an ordered keySteps[] structure.
//   Session 19AE: full .describe() coverage.
//   Session 19AN: keySteps upgraded from string[] to WorkedStep[] (richer
//     structure: intermediate expression + justification + optional
//     prerequisite reference). Old artefacts (string[]) still parse via
//     `.passthrough()` plus a runtime-tolerant view-side renderer that
//     accepts both shapes during the migration window.
//   19BH Day 2 Step 2.4: ItemTags base · v3 caseIds.min(2).optional() folds
//     into ItemTagsSchema.caseIds (required array, 1+ elements OK).
import { z } from 'zod';
import { CognitiveLevelSchema } from './cognitive-level.js';
import { SelfAssessmentDimensionSchema } from './self-assessment.js';
import { ChunkRefSchema } from './source-ref.js';
import { ItemTagsSchema, coerceItemTagsV3ToV4 } from './item-tags.js';
import { ConceptOmissionSchema } from './concept-omission.js';
/**
 * One step in a derivation. Three layers, each load-bearing for pedagogy:
 *   - intermediate:    the *what* of this step (the equation / state / claim)
 *   - justification:   the *why* (which axiom / theorem / previous step)
 *   - prerequisiteRef: optional pointer to a glossary term, theorem name, or
 *                       previous step id (e.g. "step 2", "Master Theorem")
 *
 * Per LLM_NATIVE_ARCHITECTURE §2.1 (provenance) all three are llm_generated
 * static content; per §2.2 visibility gate the renderer hides them behind
 * a per-step "reveal" until the user clicks (proof-drills-view 19AN).
 */
export const WorkedStepSchema = z.object({
    intermediate: z.string().min(1)
        .describe('The mathematical / logical content of this step (equation, claim, state). Markdown + LaTeX allowed; prefer $...$ for inline math, $$...$$ for blocks.'),
    justification: z.string().min(1)
        .describe('Why this step is valid: cite the axiom / theorem / previous step / definition. One sentence is enough; resist temptation to merge with intermediate.'),
    prerequisiteRef: z.string().optional()
        .describe('Optional pointer for cross-link rendering: glossary id (e.g. `master_theorem`), step number ("step 2"), or theorem name. The view may render this as a link / chip.'),
}).passthrough();
/** Backwards-compatible step: accept either a plain string (v1 / legacy) or a
 *  WorkedStep object (v2 / 19AN+). View-side normalizes via `normalizeStep`. */
export const ProofStepSchema = z.union([z.string().min(1), WorkedStepSchema]);
/** Normalize any step (string or WorkedStep) to a WorkedStep for uniform
 *  rendering. Legacy string steps get justification "—" so the renderer
 *  doesn't crash on missing fields. */
export function normalizeStep(step) {
    if (typeof step === 'string') {
        return { intermediate: step, justification: '—' };
    }
    return step;
}
export const ProofDrillSchema = z.preprocess(coerceItemTagsV3ToV4, ItemTagsSchema.extend({
    id: z.string().regex(/^proof[a-z0-9_-]*$/, 'proof id must start with "proof"')
        .describe('Unique snake_case slug starting with `proof` (e.g. `proof_master_theorem_01`).'),
    /** @deprecated v3 alias of caseIds[0]. */
    caseId: z.string().min(1).nullable().optional()
        .describe('@deprecated v3 alias of caseIds[0]. v4 unifies intra/cross-chapter under caseIds[] · use that.'),
    /** @deprecated v3 alias of topicIds[0]. */
    topicId: z.string().nullable().optional()
        .describe('@deprecated v3 alias of topicIds[0].'),
    subtopicId: z.string().nullable().optional()
        .describe('Subtopic id with topic prefix. null/omit if not applicable.'),
    prompt: z.string().min(30)
        .describe('The theorem statement / problem to prove. ≥30 chars. Use $...$ for inline math, $$...$$ for blocks.'),
    keySteps: z.array(ProofStepSchema).min(3)
        .describe('Ordered derivation steps. ≥3 steps (was min 1 — bumped Session 19AO; 1-2 step "proof" is rarely a proof). Each step is preferably a WorkedStep object {intermediate, justification, prerequisiteRef?}; legacy plain strings still accepted. 3-12 steps typical.'),
    commonMistakes: z.array(z.string().min(1)).nullish()
        .describe('Optional: typical errors students make on this proof. Format: "[Wrong approach] → [Why it fails]".'),
    difficulty: z.enum(['easy', 'medium', 'hard'])
        .describe('easy = direct application of formula; medium = 2-3 step derivation; hard = clever lemma + induction.'),
    cognitiveLevel: CognitiveLevelSchema.optional()
        .describe('Bloom level (proofs typically apply / analyze / evaluate).'),
    selfAssessmentRubric: z.array(SelfAssessmentDimensionSchema).min(2).max(5).nullish()
        .describe('Session 19AO: 2-5 rubric dimensions for users to self-grade their derivation attempt (e.g. logical correctness / step completeness / notation rigor). Optional for backward-compat.'),
    /** @deprecated v3 alias of conceptIds[0]. */
    conceptTag: z.string().min(1).max(80).nullish()
        .describe('@deprecated v3 alias of conceptIds[0]. Use conceptIds[].'),
    sourceRef: ChunkRefSchema.nullish()
        .describe('19BG+2 Phase 5 (DEBT-AW-SOURCEREF): citation anchor for the proof prompt source. Optional for back-compat.'),
    omittedConcepts: z.array(ConceptOmissionSchema).nullish()
        .describe('K18 soft escape · concepts the LLM declared materially insufficient for this stage.'),
}).passthrough());
//# sourceMappingURL=proof-drill.js.map